import { usePage } from "@inertiajs/react"
import { useMemo } from "react"
import {
  buildForwardHref,
  resolveBackHref,
} from "@/lib/return-to"

// Inertia's `usePage().url` is the current
// path-plus-search and rerenders on every visit, so a
// hook depending on it stays in sync without listening
// to `popstate` ourselves. This is the canonical
// "current URL" source on the React side — do not read
// `window.location` from a render path.
function useCurrentHref(): string {
  const { url } = usePage()
  return url
}

export interface UseReturnToOptions {
  // Where back goes when no return_to / legacy param is
  // in the URL. Almost always the page's parent index
  // ("/v2/contacts", "/v2/deals", "/v2/inbox").
  fallback: string
}

export interface UseReturnToResult {
  // The href the back chevron should point at. Reads
  // return_to from the current URL; falls back to the
  // legacy from_conversation_id (with a console.warn);
  // falls back to `fallback`.
  backHref: string

  // True when back is going somewhere other than the
  // fallback — the show page can adjust its back label
  // ("Conversation" vs. "Contacts") accordingly.
  hasReturnTo: boolean

  // Wraps a destination URL with the current page as
  // `return_to`, so the next show page can return here.
  // Pass the result directly to <Link href={...}>.
  forward(destination: string): string
}

// Resolves back navigation and forward-link construction
// from the URL chain established by `return_to`.
//
// Usage:
//   const { backHref, forward } = useReturnTo({ fallback: "/v2/contacts" })
//   // ...
//   <SmartBackLink href={backHref} fallbackLabel="Contacts" returnLabel="Back" />
//   <Link href={forward(`/v2/deals/${deal.id}`)}>Open deal</Link>
//
// See `app/frontend/lib/return-to.ts` for the chain
// composition rules.
export function useReturnTo({
  fallback,
}: UseReturnToOptions): UseReturnToResult {
  const currentHref = useCurrentHref()

  return useMemo(() => {
    const queryStart = currentHref.indexOf("?")
    const search = queryStart >= 0 ? currentHref.slice(queryStart) : ""
    const backHref = resolveBackHref({ search, fallback })
    return {
      backHref,
      hasReturnTo: backHref !== fallback,
      forward(destination: string) {
        return buildForwardHref({ destination, currentHref })
      },
    }
  }, [currentHref, fallback])
}
